import { gql, type TypedDocumentNode } from '@apollo/client';
import type { CSSProperties } from 'react';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { DiamondPatternBackground } from '@sorare/core/src/atoms/ui/DiamondPatternBackground';
import { AbsoluteNav } from '@sorare/core/src/components/navigation/AppNavigation/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove.ts';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery.ts';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql/index.ts';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import { SeriesBoardHeader } from '#src/components/series/SeriesBoard/SeriesBoardHeader/index.tsx';
import {
  DIAMOND_HEIGHT,
  DIAMOND_WIDTH,
} from '#src/components/series/SeriesBoard/utils.ts';
import { SeriesMissions } from '#src/components/series/SeriesMissions/index.tsx';
import { useBoardScroll } from '#src/hooks/useBoardScroll.ts';

import { GuessTheScoreBadge } from '../GuessTheScoreBadge/index.tsx';
import { ScoreBlock } from '../ScoreBlock/index.tsx';
import { SquadMembersButton } from '../SquadMembersButton';
import { SquadStepTile } from './SquadStepTile/index.tsx';
import type {
  SquadBoard_currentUser,
  SquadBoardSeriesMissionsQuery,
  SquadBoardSeriesMissionsQueryVariables,
} from './__generated__/index.graphql.ts';
import field from './assets/field.webp';

const NAV_CLEARANCE = 11 * 8;
const SCORE_BLOCK_HEIGHT = 22 * 8;
const FIRST_ELEMENT_BOTTOM =
  NAV_CLEARANCE + SCORE_BLOCK_HEIGHT + 2 * DIAMOND_HEIGHT;
const RIGHT_COLUMN_WIDTH = 420;
const HEADER_HEIGHT = 18 * 8;

const Root = styled.div`
  position: relative;
  height: calc(var(--100vh) - var(--current-stack-height));
  width: 100%;
`;
const ScrollableContainer = styled.div`
  scroll-snap-type: y mandatory;
  overflow-y: auto;
  height: 100%;
  scroll-padding-bottom: ${FIRST_ELEMENT_BOTTOM}px;
`;
const StyledDiamondPatternBackground = styled(DiamondPatternBackground)`
  pointer-events: none;
`;
const Board = styled(Vertical).attrs({ center: true })`
  position: relative;
  width: 100%;
  background-image:
    url(${field}), linear-gradient(180deg, #2f066e 0%, #2f066e 100%);
  background-size:
    auto
      calc(
        ${FIRST_ELEMENT_BOTTOM}px + (var(--number-of-rows) - 0.5) *
          var(--row-height)
      ),
    100% 100%;
  background-position:
    center bottom,
    center center;
  background-repeat: no-repeat, no-repeat;
  @media ${desktopAndAbove} {
    padding-right: ${RIGHT_COLUMN_WIDTH}px;
    background-position:
      calc(50% - ${RIGHT_COLUMN_WIDTH / 2}px) bottom,
      center center;
  }
`;
const DesktopMissionsContainer = styled(Vertical).attrs({ gap: 5 })`
  padding-top: var(--double-and-a-half-unit);
  padding-right: var(--double-unit);
  height: 100%;
  width: ${RIGHT_COLUMN_WIDTH}px;
  position: absolute;
  right: 0;
  bottom: 0;
  top: 0;
  background: linear-gradient(
    to left,
    rgba(var(--c-rgb-black), 0.8) 0%,
    rgba(var(--c-rgb-black), 0.8) 50%,
    transparent ${RIGHT_COLUMN_WIDTH}px
  );
  z-index: 1;
`;
const BoardContent = styled(Vertical).attrs({ gap: 0 })`
  display: flex;
  position: relative;
  z-index: 0;
  flex-direction: column-reverse;
  padding-bottom: ${FIRST_ELEMENT_BOTTOM}px;
  padding-top: calc(
    var(--100vh) - ${FIRST_ELEMENT_BOTTOM}px - var(--row-height)
  );
  margin-top: auto;
`;
const Row = styled(Vertical)`
  height: var(--row-height);
  align-items: center;
  justify-content: flex-end;
  position: relative;
  pointer-events: none;
  > * {
    pointer-events: auto;
  }
  scroll-snap-align: end;
`;
const LineSvg = styled.svg`
  position: absolute;
  z-index: -1;
  bottom: ${DIAMOND_HEIGHT / 2}px;
  left: 50%;
  transform: translateX(-50%);
  height: var(--row-height);
  width: 6px;
  overflow: visible;
  pointer-events: none;
`;
const Line = styled.line`
  fill: none;
  stroke: black;
  stroke-opacity: 0.5;
  stroke-width: 6;
`;
const HeaderWrapper = styled(Vertical)`
  background: linear-gradient(
    to bottom,
    rgba(var(--c-rgb-black), 1) 0%,
    rgba(var(--c-rgb-black), 0.57) 80%,
    transparent 100%
  );
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  width: 100%;
`;
const SquadMembersWrapper = styled.div`
  align-self: flex-end;
  padding-right: var(--unit);

  @media ${desktopAndAbove} {
    padding-right: calc(${RIGHT_COLUMN_WIDTH}px + var(--unit));
  }
`;
const GuessTheScoreBadgeWrapper = styled.div`
  position: absolute;
  top: calc(8 * var(--unit));
  left: var(--double-unit);
  z-index: 2;
`;
const ScoreBlockWrapper = styled.div`
  position: absolute;
  bottom: ${NAV_CLEARANCE}px;
  left: var(--double-unit);
  right: var(--double-unit);
  z-index: 1;
  width: calc(100% - 2 * var(--double-unit));
  height: ${SCORE_BLOCK_HEIGHT}px;

  @media ${desktopAndAbove} {
    max-width: 400px;
    left: calc(50% - ${RIGHT_COLUMN_WIDTH / 2}px);
    transform: translateX(-50%);
  }
`;

const SQUAD_BOARD_SERIES_MISSIONS_QUERY = gql`
  query SquadBoardSeriesMissionsQuery($sport: Sport!) {
    currentUser {
      slug
      ...SeriesMissions_currentUser
    }
  }
  ${SeriesMissions.fragments.currentUser}
` as TypedDocumentNode<
  SquadBoardSeriesMissionsQuery,
  SquadBoardSeriesMissionsQueryVariables
>;

type Props = {
  currentUser: SquadBoard_currentUser;
};
export const SquadBoard = ({ currentUser }: Props) => {
  const { sport } = useSportContext();
  const { squad, setTasksTrack, setBoard, setGuessTheScore } = currentUser;
  const board = isType(setBoard, 'SquadBoard') ? setBoard : undefined;
  const steps = (board?.steps || []).filter(isTypeFn('SquadStep'));
  //first non-acknowledged step is the current step
  const currentStepIndex = steps.findIndex(step => !step.acknowledged);
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const { data } = useQuery(SQUAD_BOARD_SERIES_MISSIONS_QUERY, {
    variables: {
      sport,
    },
    skip: !isDesktopAndAbove,
  });

  const [rootRef, { height: totalHeight }] = useMeasure<HTMLDivElement>();

  const availableHeightWithoutUIElements =
    totalHeight - FIRST_ELEMENT_BOTTOM - HEADER_HEIGHT;
  const overhang = 8 * 4;

  const rowHeight = Math.max(
    DIAMOND_HEIGHT,
    Math.floor((availableHeightWithoutUIElements - overhang) / DIAMOND_HEIGHT) *
      DIAMOND_HEIGHT
  );

  const { scrollContainerRef, activeIndex } = useBoardScroll({
    firstElementBottom: FIRST_ELEMENT_BOTTOM,
    rowHeight,
    currentStepIndex,
    boardKey: 'squad',
  });

  if (!squad) {
    return null;
  }

  const activeStep = steps.at(activeIndex);

  return (
    <Root
      ref={rootRef}
      style={
        {
          '--row-height': `${rowHeight}px`,
          '--number-of-rows': `${steps.length}`,
        } as CSSProperties
      }
    >
      <HeaderWrapper>
        <SeriesBoardHeader
          squad={squad}
          trck={setTasksTrack}
          selectedTrack="squad"
        />
        <SquadMembersWrapper>
          <SquadMembersButton squad={squad} />
        </SquadMembersWrapper>
        {!!setGuessTheScore && (
          <GuessTheScoreBadgeWrapper>
            <GuessTheScoreBadge task={setGuessTheScore} />
          </GuessTheScoreBadgeWrapper>
        )}
      </HeaderWrapper>
      <ScoreBlockWrapper>
        {!!activeStep && (
          <ScoreBlock
            step={activeStep}
            height={SCORE_BLOCK_HEIGHT}
            squadBoard={board}
          />
        )}
      </ScoreBlockWrapper>
      {!!isDesktopAndAbove && !!data?.currentUser && (
        <DesktopMissionsContainer>
          <SeriesMissions
            currentUser={data.currentUser}
            showFeaturedCollectionTask={true}
          />
        </DesktopMissionsContainer>
      )}
      <ScrollableContainer ref={scrollContainerRef}>
        <AbsoluteNav />
        <Board>
          <StyledDiamondPatternBackground
            width={DIAMOND_WIDTH}
            height={DIAMOND_HEIGHT}
            startFromBottom
          />
          <BoardContent>
            {steps.map((step, index) => (
              <Row key={step.id} data-step-index={index}>
                <SquadStepTile
                  step={step}
                  isCurrentStep={index === currentStepIndex}
                />
                {index < steps.length - 1 && (
                  <LineSvg>
                    <Line x1="50%" y1={0} x2="50%" y2="100%" />
                  </LineSvg>
                )}
              </Row>
            ))}
          </BoardContent>
        </Board>
      </ScrollableContainer>
    </Root>
  );
};

SquadBoard.fragments = {
  currentUser: gql`
    fragment SquadBoard_currentUser on CurrentUser {
      slug
      squad(sport: $sport) {
        id
        ...SeriesBoardHeader_squad
        ...SquadMembersButton_squad
      }
      setTasksTrack(sport: $sport) {
        id
        ...SeriesBoardHeader_tasksTrack
      }
      setGuessTheScore(sport: $sport) {
        id
        ...GuessTheScoreBadge_taskInterface
      }
      setBoard(mode: SQUAD, sport: $sport) {
        id
        steps {
          id
          ... on SquadStep {
            id
            acknowledged
            ...SquadStepTile_squadStep
            ...ScoreBlock_squadStep
          }
        }
        ...ScoreBlock_squadBoard
      }
    }
    ${SeriesBoardHeader.fragments.tasksTrack}
    ${SeriesBoardHeader.fragments.squad}
    ${SquadMembersButton.fragments.squad}
    ${GuessTheScoreBadge.fragments.taskInterface}
    ${SquadStepTile.fragments.squadStep}
    ${ScoreBlock.fragments.squadStep}
    ${ScoreBlock.fragments.squadBoard}
  ` as TypedDocumentNode<SquadBoard_currentUser>,
};
